import { useState, useEffect, useCallback } from 'react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { MessageCircle } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useUnreadCountQuery } from '@/queries/conversations/useConversationsQuery'
import { InboxModal } from './InboxModal'
import { useChatBubble } from './ChatBubbleProvider'

interface FloatingChatBubbleProps {
	onOpenInbox: () => void
	className?: string
}

export function FloatingChatBubble({ 
	onOpenInbox,
	className 
}: FloatingChatBubbleProps) {
	const [horizontalPosition, setHorizontalPosition] = useState(24) // Only track right position
	const [verticalPosition, setVerticalPosition] = useState(12) // Fixed vertical, will change based on modal state
	const [isDragging, setIsDragging] = useState(false)
	const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 })
	const [hasDragged, setHasDragged] = useState(false)
	const [dragStartPos, setDragStartPos] = useState({ x: 0, y: 0 })
	const [isMouseDown, setIsMouseDown] = useState(false)
	const [isInboxModalOpen, setIsInboxModalOpen] = useState(false)
	const [inboxMode, setInboxMode] = useState<'fullscreen' | 'panel'>('panel')
	const { state: chatState } = useChatBubble()

	// Sync bubble position with global state
	useEffect(() => {
		if (chatState.bubblePosition) {
			setVerticalPosition(chatState.bubblePosition.bottom)
			setHorizontalPosition(chatState.bubblePosition.right)
		}
	}, [chatState.bubblePosition])

	// Sync bubble visibility with global state
	useEffect(() => {
		if (!chatState.isVisible) {
			setIsInboxModalOpen(false)
		}
	}, [chatState.isVisible])

	// Get real-time unread count
	const { data: unreadCount = 0 } = useUnreadCountQuery()

	// Handle opening inbox modal (panel-sized from chat bubble)
	const handleOpenInboxPanel = useCallback(() => {
		setIsInboxModalOpen(true)
		setInboxMode('panel')
		// Move bubble to 6px from bottom for panel mode
		setVerticalPosition(6)
		
		// Auto-correct horizontal position if it's past the max left boundary
		const maxLeftBoundary = window.innerWidth - 522 // Same calculation as drag boundary
		if (horizontalPosition > maxLeftBoundary) {
			setHorizontalPosition(maxLeftBoundary)
		}
		
		// Also call the parent onOpenInbox to update global state
		onOpenInbox()
	}, [onOpenInbox, horizontalPosition])

	// Handle opening full-screen inbox (from sidebar)
	const handleOpenInboxFullscreen = useCallback(() => {
		setIsInboxModalOpen(true)
		setInboxMode('fullscreen')
		// Move bubble to bottom-right corner: 6px from bottom AND 6px from right
		setVerticalPosition(6)
		setHorizontalPosition(6)
		// Also call the parent onOpenInbox to update global state
		onOpenInbox()
	}, [onOpenInbox])

	// Handle toggling between fullscreen and panel modes
	const handleToggleMode = useCallback(() => {
		if (inboxMode === 'panel') {
			// Switch to fullscreen
			setInboxMode('fullscreen')
			setVerticalPosition(6)
			setHorizontalPosition(6)
		} else {
			// Switch to panel
			setInboxMode('panel')
			setVerticalPosition(6)
			// Keep current horizontal position for panel mode
		}
	}, [inboxMode])

	// Listen for external openInbox calls (from sidebar - fullscreen)
	useEffect(() => {
		const handleOpenInboxEvent = () => {
			handleOpenInboxFullscreen()
		}

		window.addEventListener('openInboxModal', handleOpenInboxEvent)
		return () => window.removeEventListener('openInboxModal', handleOpenInboxEvent)
	}, [handleOpenInboxFullscreen])

	// Listen for floating inbox calls (from order selection)
	useEffect(() => {
		const handleOpenFloatingInboxEvent = () => {
			handleOpenInboxPanel()
		}

		window.addEventListener('openFloatingInbox', handleOpenFloatingInboxEvent)
		return () => window.removeEventListener('openFloatingInbox', handleOpenFloatingInboxEvent)
	}, [handleOpenInboxPanel])

	const handleCloseInbox = () => {
		setIsInboxModalOpen(false)
		// Return bubble to default position when inbox closes (equidistant from bottom and right)
		setVerticalPosition(12)
		// Only reset horizontal position if closing from fullscreen mode
		if (inboxMode === 'fullscreen') {
			setHorizontalPosition(12) // Set right position to match bottom position for fullscreen
		}
		// For portable mode, keep the current horizontal position (user's dragged position)
	}

	// Special handler for when order panel closes - always reset to default position
	const handleCloseInboxFromOrderPanel = () => {
		setIsInboxModalOpen(false)
		// Always reset to default position when order panel closes
		setVerticalPosition(12)
		setHorizontalPosition(12)
	}

	// Listen for close inbox modal events (from order panel close)
	useEffect(() => {
		const handleCloseInboxModalEvent = () => {
			handleCloseInboxFromOrderPanel()
		}

		window.addEventListener('closeInboxModal', handleCloseInboxModalEvent)
		return () => window.removeEventListener('closeInboxModal', handleCloseInboxModalEvent)
	}, [handleCloseInboxFromOrderPanel])

	const handleMinimizeInbox = () => {
		setIsInboxModalOpen(false)
		// Return bubble to default position when inbox closes
		setVerticalPosition(12)
		// Keep current horizontal position (don't reset for minimize - user dragged it there)
	}

	// Handle dragging for repositioning
	const handleMouseDown = (e: React.MouseEvent) => {
		setIsMouseDown(true) // Track mouse down but don't change cursor yet
		setHasDragged(false) // Reset drag flag
		setDragStartPos({ x: e.clientX, y: e.clientY }) // Track starting position
		const rect = e.currentTarget.getBoundingClientRect()
		setDragOffset({
			x: e.clientX - rect.left,
			y: e.clientY - rect.top
		})
		// Don't set cursor yet - wait for actual drag to start
	}

	useEffect(() => {
		const handleMouseMove = (e: MouseEvent) => {
			// Check if we've moved far enough to initiate drag (threshold: 6px)
			const dragDistance = Math.sqrt(
				Math.pow(e.clientX - dragStartPos.x, 2) + Math.pow(e.clientY - dragStartPos.y, 2)
			)
			
			if (!isDragging && dragDistance > 6) {
				// Start dragging only after moving 6px
				setIsDragging(true)
				// Set global grabbing cursor when actual dragging starts
				document.body.style.cursor = 'grabbing'
			}
			
			if (isDragging) {
				// Mark that we've dragged (for click vs drag detection)
				setHasDragged(true)
				
				// Only allow horizontal movement
				const newRight = window.innerWidth - e.clientX + dragOffset.x - 60 // 60px bubble width
				
				if (isInboxModalOpen) {
					// When inbox is open, enforce boundaries to keep window on screen
					const maxRightPosition = 24 // Can't be closer than 24px to right edge
					const minRightPosition = window.innerWidth - 522 // Ensures exactly 24px left margin for window
					
					setHorizontalPosition(Math.max(maxRightPosition, Math.min(newRight, minRightPosition)))
				} else {
					// When inbox is closed, allow free dragging with minimal boundaries
					const maxRightPosition = 24 // Can't be closer than 24px to right edge
					const minRightPosition = window.innerWidth - 84 // Just keep bubble on screen (60px bubble + 24px margin)
					
					setHorizontalPosition(Math.max(maxRightPosition, Math.min(newRight, minRightPosition)))
				}
			}
		}

		const handleMouseUp = () => {
			setIsDragging(false)
			setIsMouseDown(false) // Reset mouse down state
			setDragStartPos({ x: 0, y: 0 }) // Reset drag start position
			
			// Reset document body cursor
			document.body.style.cursor = ''
			
			// Save horizontal position to localStorage (only save user-dragged position)
			if (hasDragged && !isInboxModalOpen) {
				localStorage.setItem('chatBubbleHorizontalPosition', horizontalPosition.toString())
			}
		}

		if (dragStartPos.x !== 0 || dragStartPos.y !== 0) {
			document.addEventListener('mousemove', handleMouseMove)
			document.addEventListener('mouseup', handleMouseUp)
		}

		return () => {
			document.removeEventListener('mousemove', handleMouseMove)
			document.removeEventListener('mouseup', handleMouseUp)
		}
	}, [isDragging, dragOffset, horizontalPosition, dragStartPos, isInboxModalOpen, hasDragged, isMouseDown])

	// Load saved horizontal position on mount
	useEffect(() => {
		const savedHorizontalPosition = localStorage.getItem('chatBubbleHorizontalPosition')
		if (savedHorizontalPosition) {
			try {
				const parsed = parseInt(savedHorizontalPosition, 10)
				if (!isNaN(parsed)) {
					setHorizontalPosition(parsed)
				}
			} catch (error) {
				console.error('Failed to parse saved chat bubble horizontal position:', error)
			}
		}
	}, [])


	return (
		<>
			{/* Chat Bubble - Always visible */}
				<div 
					data-chat-bubble
					className={cn(
						"fixed z-[110] transition-all duration-200 hover:scale-105",
						// Scale effect only when dragging (cursor handled by inline style)
						isDragging && "scale-105",
						className
					)}
					style={{ 
						bottom: isDragging ? '18px' : `${verticalPosition}px`, // Elevate to 18px when dragging
						right: `${horizontalPosition}px`,
						cursor: isDragging ? 'grabbing' : 'pointer', // Only grabbing when actually dragging
						transition: isDragging ? 'none' : 'bottom 0.3s ease-in-out, right 0.3s ease-in-out' // Smooth transitions except when dragging
					}}
					onMouseDown={handleMouseDown}
					onClick={(e) => {
						console.log('💬 ChatBubble: Click detected on bubble', {
							hasDragged,
							isInboxModalOpen,
							target: e.target,
							currentTarget: e.currentTarget,
							stackTrace: new Error().stack
						})
						if (!hasDragged) {
							if (isInboxModalOpen) {
								handleCloseInbox()
							} else {
								handleOpenInboxPanel()
							}
						}
					}}
				>
					<div className="relative">
						{/* Outer wrapper applies drop-shadow so we avoid Tailwind's box-shadow composition quirks */}
						<div
							className="transition-all duration-300 rounded-full"
							style={{
								boxShadow: isDragging
									? 'rgba(0, 0, 0, 0.7) 0 8px 25px 0, rgba(0, 0, 0, 0.4) 0 8px 25px 0' // Elevated shadow when dragging
									: isInboxModalOpen
										? 'rgba(0, 0, 0, 0.6) 0 2px 5px 0, rgba(0, 0, 0, 0.6) 0 2px 5px 0' // Landed shadow
										: 'rgba(0, 0, 0, 0.6) 0 5px 15px 0, rgba(0, 0, 0, 0.35) 0 5px 15px 0', // Floating shadow
								transition: isDragging ? 'none' : 'box-shadow 300ms ease-in-out'
							}}
						>
						<Button
							size="lg"
							className={cn(
								"h-14 w-14 rounded-full bg-primary hover:bg-primary/90 transition-all duration-300 shadow-none"
							)}
							style={{ cursor: 'inherit' }} // Force cursor inheritance via inline style
						>
								<MessageCircle className="h-6 w-6" />
							</Button>
						</div>
						
						{unreadCount > 0 && (
							<Badge 
								variant="destructive" 
								className={cn(
									"absolute -top-1 -right-1 h-6 w-6 rounded-full p-0 flex items-center justify-center text-xs font-bold transition-shadow duration-300 bg-rose-700 hover:bg-rose-700",
									// Badge shadow matches bubble state
									isInboxModalOpen ? "shadow-sm" : "shadow-md"
								)}
							>
								{unreadCount > 99 ? '99+' : unreadCount}
							</Badge>
						)}
					</div>
				</div>

			{/* Inbox Modal */}
			<InboxModal
				isOpen={isInboxModalOpen}
				onClose={handleCloseInbox}
				onMinimize={handleMinimizeInbox}
				onToggleMode={handleToggleMode}
				bubblePosition={{ bottom: verticalPosition, right: horizontalPosition }}
				mode={inboxMode}
				isDragging={isDragging}
				orderId={chatState.activeOrderId}
				onCloseAll={() => {
					// Close both panels and reset chat bubble position
					// We need to close the inbox modal locally and let the global state handle the rest
					setIsInboxModalOpen(false)
					// Reset bubble to default position
					setVerticalPosition(12)
					setHorizontalPosition(12)
					// Dispatch event to close order panel
					window.dispatchEvent(new CustomEvent('closeOrderPanel'))
				}}
				isOrderPanelOpen={chatState.isOrderPanelOpen || false}
			/>
		</>
	)
}
